import { useCallback, useEffect, useState } from 'react';
import Button from '../../../components/Button';
import { UserIdSearch } from '../../../components/UserIdSearch';
import { useAuth } from '../../../hooks/useAuth';
import type { ObsessLessUser } from '../../../types/commonTypes';
import { dailyTaskTemplateService } from '../services/dailyTaskTemplateService';
import {
  DAILY_TASK_CATEGORY_LABELS,
  DAILY_TASK_STATUS_OPTIONS,
  DAY_KIND_LABELS,
} from '../services/dailyTaskTemplateTypes';
import type {
  DailyTasksState,
  DailyTaskStatus,
  GeneratedDailyTask,
} from '../services/dailyTaskTemplateTypes';

interface SelectedUser {
  uid: string;
  label: string;
}

const STATUS_BADGE_CLASS: Record<string, string> = {
  available: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  claimed: 'bg-purple-100 text-purple-700',
};

const StatusBadge = ({ status }: { status: string }) => (
  <span
    className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE_CLASS[status] ?? 'bg-gray-100 text-gray-600'}`}
  >
    {status}
  </span>
);

const selectClasses =
  'px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500';

interface TaskRowProps {
  task: GeneratedDailyTask;
  onUpdate: (taskId: string, patch: { status?: DailyTaskStatus; completedSteps?: number }) => Promise<void>;
}

const TaskRow = ({ task, onUpdate }: TaskRowProps) => {
  const [stepsInput, setStepsInput] = useState(String(task.completedSteps ?? 0));
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setStepsInput(String(task.completedSteps ?? 0));
  }, [task.completedSteps]);

  const handleStatusChange = async (status: DailyTaskStatus) => {
    setIsSaving(true);
    try {
      await onUpdate(task.id, { status });
    } finally {
      setIsSaving(false);
    }
  };

  const handleStepsSave = async () => {
    const parsed = Number(stepsInput);
    if (Number.isNaN(parsed) || parsed === task.completedSteps) return;
    setIsSaving(true);
    try {
      await onUpdate(task.id, { completedSteps: parsed });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
      <span className="text-xl">{task.emoji}</span>
      <div className="min-w-40 flex-1">
        <div className="text-sm font-medium text-gray-800 break-words">{task.title}</div>
        <div className="text-xs text-gray-500">
          {DAILY_TASK_CATEGORY_LABELS[task.category] ?? task.category} · {task.source} · {task.energy}⚡
        </div>
      </div>

      <StatusBadge status={task.status} />
      <select
        value={task.status}
        disabled={isSaving}
        onChange={(e) => handleStatusChange(e.target.value as DailyTaskStatus)}
        className={selectClasses}
      >
        {DAILY_TASK_STATUS_OPTIONS.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>

      <div className="flex items-center gap-1">
        <label className="text-xs text-gray-500">steps</label>
        <input
          type="number"
          value={stepsInput}
          onChange={(e) => setStepsInput(e.target.value)}
          disabled={isSaving}
          className="w-16 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <span className="text-xs text-gray-400">/ {task.totalSteps}</span>
        <Button
          onClick={handleStepsSave}
          variant="secondary"
          size="sm"
          disabled={isSaving || Number(stepsInput) === task.completedSteps}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export const DailyTaskUserTasks = () => {
  const { user } = useAuth();

  const [selectedUser, setSelectedUser] = useState<SelectedUser | null>(null);
  const [dailyTasks, setDailyTasks] = useState<DailyTasksState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // Default to the logged-in admin.
  useEffect(() => {
    if (!selectedUser && user) {
      setSelectedUser({ uid: user.uid, label: user.email ?? user.displayName ?? user.uid });
    }
  }, [user, selectedUser]);

  const loadTasks = useCallback(async (uid: string) => {
    setIsLoading(true);
    try {
      const state = await dailyTaskTemplateService.getUserDailyTasks(uid);
      setDailyTasks(state);
    } catch (error) {
      console.error('Failed to load user daily tasks:', error);
      alert('Failed to load user daily tasks. Check the console for details.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedUser) {
      void loadTasks(selectedUser.uid);
    }
  }, [selectedUser, loadTasks]);

  const refresh = useCallback(async () => {
    if (selectedUser) await loadTasks(selectedUser.uid);
  }, [selectedUser, loadTasks]);

  const handleSearchResult = async (found: ObsessLessUser | null) => {
    if (!found) return;
    setSelectedUser({ uid: found.uid, label: found.email ?? found.displayName ?? found.uid });
  };

  const handleBackToMe = () => {
    if (user) {
      setSelectedUser({ uid: user.uid, label: user.email ?? user.displayName ?? user.uid });
    }
  };

  const handleTaskUpdate = async (
    taskId: string,
    patch: { status?: DailyTaskStatus; completedSteps?: number },
  ) => {
    if (!selectedUser) return;
    try {
      await dailyTaskTemplateService.updateUserDailyTask(selectedUser.uid, taskId, patch);
      await refresh();
    } catch (error) {
      console.error('Failed to update daily task:', error);
      alert('Failed to update daily task. Check the console for details.');
    }
  };

  const handleReset = async () => {
    if (!selectedUser) return;

    const confirmed = window.confirm(
      `Reset daily tasks for ${selectedUser.label}? This will delete all generated tasks and pending queue items. This cannot be undone.`,
    );
    if (!confirmed) return;

    setIsResetting(true);
    try {
      await dailyTaskTemplateService.resetUserDailyTasks(selectedUser.uid);
      alert('Daily tasks reset successfully.');
      await refresh();
    } catch (error) {
      console.error('Failed to reset daily tasks:', error);
      alert('Failed to reset daily tasks. Check the console for details.');
    } finally {
      setIsResetting(false);
    }
  };

  const isViewingSelf = !!user && selectedUser?.uid === user.uid;
  const tasks = dailyTasks
    ? Object.values(dailyTasks.tasks ?? {}).sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : [];

  return (
    <div>
      {/* User picker */}
      <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-xs font-medium text-gray-500">Viewing daily tasks for</p>
            <p className="font-semibold text-gray-900">{selectedUser?.label ?? '—'}</p>
            <p className="font-mono text-xs text-gray-500 break-all">{selectedUser?.uid}</p>
          </div>
          {!isViewingSelf && (
            <Button variant="outline" size="sm" onClick={handleBackToMe}>
              Back to my tasks
            </Button>
          )}
        </div>
        <UserIdSearch onSearchResult={handleSearchResult} />
      </div>

      {isLoading && <p className="text-gray-500">Loading daily tasks...</p>}

      {!isLoading && selectedUser && (
        <>
          {dailyTasks ? (
            <>
              {/* State header */}
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-4">
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-700">
                  <span>
                    <span className="text-gray-500">Date:</span> {dailyTasks.generatedForDate}
                  </span>
                  <span>
                    <span className="text-gray-500">Day kind:</span>{' '}
                    {DAY_KIND_LABELS[dailyTasks.dayKind] ?? dailyTasks.dayKind}
                  </span>
                  <span>
                    <span className="text-gray-500">Tasks:</span> {tasks.length}
                  </span>
                </div>
                <button
                  onClick={handleReset}
                  disabled={isResetting}
                  className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isResetting ? 'Resetting...' : 'Reset Daily Tasks'}
                </button>
              </div>

              {/* Task list */}
              {tasks.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {tasks.map((task) => (
                    <TaskRow key={task.id} task={task} onUpdate={handleTaskUpdate} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">This user has no daily tasks for the current day.</p>
              )}
            </>
          ) : (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
              This user has no daily tasks state yet. They need to open the app to generate today's tasks.
            </div>
          )}
        </>
      )}
    </div>
  );
};
